'use client'

import { CSSProperties, useEffect, useState } from 'react'
import { shallowEqual } from '@xstate/react'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'
import { twMerge } from 'tailwind-merge'

import { usePrevious } from '@/utils/usePrevious'
import videoPlaceholder from '../../assets/video-placeholder.png'
import { PreventScroll } from '../PreventScroll'
import { VideoPlayer } from '../Video'
import { CloseButton } from './CloseButton'
import { convertYouTubeToEmbed, embedClassName } from './embed'
import { ModalGalleryContext } from './GalleryContext'
import { NavButton } from './NavButton'

interface ModalGalleryProps {
  className?: string
}

// Force all rex images to use the unoptimized version (slow to get local next to optimize)
const unoptimizedRemoteImages = true

export function ModalGallery({ className }: ModalGalleryProps) {
  const [, sendToModalGallery] = ModalGalleryContext.useActor()
  const { items, index, active, nextDisabled, prevDisabled } = ModalGalleryContext.useSelector((state) => {
    const { index, items, galleryApi } = state.context
    const active = state.matches('Active')
    return {
      index,
      items,
      active,
      nextDisabled: !galleryApi?.canScrollNext(),
      prevDisabled: !galleryApi?.canScrollPrev(),
    }
  }, shallowEqual)

  const [visiblyHidden, setVisiblyHidden] = useState(!active)

  // We need to hide things after transitions. So to handle this we track active and fire when it changes.
  const prevActive = usePrevious(active)
  useEffect(() => {
    // The important one, delay hiding everything when we go from being active to inactive.
    if (prevActive !== active) {
      if (!active) {
        const timeout = setTimeout(() => {
          setVisiblyHidden(true)
        }, 700)
        return () => clearTimeout(timeout)
      }
      // Otherwise, don't care, we are visible.
      setVisiblyHidden(false)
    }
  }, [active, prevActive])

  const [galleryRef, galleryApi] = useEmblaCarousel({ containScroll: false })
  const [thumbnailsRef, thumbnailsApi] = useEmblaCarousel({ containScroll: false })

  useEffect(() => {
    const selectHandler = () => {
      const index = galleryApi?.selectedScrollSnap() || 0
      sendToModalGallery({ type: 'Go to item', index })
    }

    if (galleryApi && thumbnailsApi) {
      sendToModalGallery({ type: 'Set APIs', galleryApi, thumbnailsApi })
      galleryApi.on('select', selectHandler)
    }

    return () => {
      galleryApi?.off('select', selectHandler)
    }
  }, [galleryApi, sendToModalGallery, thumbnailsApi])

  // Determine whether we show everything, when we aren't visibly hidden and are active.
  // This allows for the initial 'active', and show's the modal in an inactive state.
  // Then the above useEffect cycles and will set visiblyHidden to false, then once we aren't hidden and active, we really start
  // to show the modal, which then means animations all work.
  // As soon as we are inactive, that disables show, so we can hide everything nicely.
  // Once visiblyHidden is true, we can hide everything properly.
  const show = !visiblyHidden && active
  return (
    <div
      className={twMerge(
        'fixed inset-0 z-modal grid h-screen w-screen select-none grid-rows-[1fr_3rem] gap-y-6 overflow-hidden py-4 transition-[background,filter] xs:grid-rows-[1fr_4rem] sm:grid-rows-[1fr_6rem] vsm:gap-y-8 vsm:pt-8 vmd:gap-y-12',
        'before:pointer-events-none before:fixed before:left-0 before:z-30 before:h-full before:w-1/4 before:bg-black/50 before:bg-blend-darken before:gradient-mask-r-10',
        'after:pointer-events-none after:fixed after:right-0 after:z-30 after:h-full after:w-1/4 after:bg-black/50 after:bg-blend-darken after:gradient-mask-l-10',
        show
          ? 'pointer-events-auto bg-neutral-850/90 backdrop-blur duration-1000'
          : 'pointer-events-none bg-neutral-850/0 backdrop-blur-0 duration-700 before:hidden after:hidden',
        !active && visiblyHidden && 'hidden',
        className,
      )}
    >
      <div
        className={twMerge(
          'w-screen py-8 backface-hidden vsm:py-8 vmd:py-12 vlg:py-16',
          show ? 'opacity-100 delay-200 duration-1000' : 'opacity-0 delay-0 duration-300',
        )}
        ref={galleryRef}
      >
        <div className="flex h-full items-stretch justify-start gap-x-2 lg:gap-x-16">
          {items.map((item, i) => {
            const image = item.kind === 'video' ? item.videoCover?.[0] ?? { width: 1920, height: 1080 } : item
            const remoteImage =
              unoptimizedRemoteImages && Number(item.url.match(/(rexsoftware)|(cloudfront)/)?.length) > 0

            const imageClasses = [
              'safari:md:rotate-y-0 safari:md:hover:rotate-y-0 safari:drop-shadow-none safari:md:scale-100 safari:md:blur-none safari:md:hover:blur-none',
              i > index ? 'md:-rotate-y-6 md:hover:-rotate-y-3' : i < index && 'md:rotate-y-6 md:hover:rotate-y-3',
              i === index
                ? 'z-10 blur-0 brightness-100 md:drop-shadow-[0_24px_36px_rgba(0,0,0,0.9)] md:scale-110'
                : 'cursor-pointer brightness-75 md:blur-[2px] md:drop-shadow-xl md:scale-90 md:hover:blur-[1px] md:hover:brightness-90 md:hover:drop-shadow-2xl md:hover:duration-300 md:hover:scale-[.92]',
            ]
            return (
              <div
                key={item.id}
                className="relative aspect-[--aspectRatio] h-full max-w-[88vw] shrink-0 grow-0 sm:max-w-[75vw] md:perspective-500 lg:max-w-[55vw]"
                style={{ '--aspectRatio': `${image.width} / ${image.height}` } as CSSProperties}
              >
                {item.type && item.type === 'iframe' && (
                  <div
                    className="relative h-full"
                    onClick={index !== i ? () => sendToModalGallery({ type: 'Go to item', index: i }) : undefined}
                  >
                    <iframe
                      src={convertYouTubeToEmbed(item.url)}
                      className={twMerge(
                        'h-full w-full border-0',
                        embedClassName(item.url),
                        index !== i && 'pointer-events-none',
                      )}
                      allowFullScreen
                    ></iframe>
                  </div>
                )}
                {item.kind === 'video' && (
                  <div className={twMerge('flex h-full items-center object-contain duration-700', ...imageClasses)}>
                    <VideoPlayer
                      src={item.url}
                      controls
                      className="aspect-video w-full"
                      playing={i === index && show}
                    />
                  </div>
                )}
                {item.kind === 'image' && (
                  <Image
                    fill
                    src={item.url}
                    alt={item.alt ?? ''}
                    title={item.caption ?? ''}
                    sizes="(max-width: 640px) 100vw, 75vw"
                    unoptimized={remoteImage}
                    blurDataURL={item.blur}
                    placeholder={item.blur ? 'blur' : 'empty'}
                    className={twMerge('object-contain duration-700', ...imageClasses)}
                    onClick={() => sendToModalGallery({ type: 'Go to item', index: i })}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
      <div
        className={twMerge('w-screen', show ? 'opacity-100 delay-300 duration-700' : 'opacity-0 delay-0 duration-300')}
        ref={thumbnailsRef}
      >
        <div className="flex h-full items-stretch justify-start gap-x-3 xs:gap-x-4 sm:gap-x-5">
          {items.map((item, i) => {
            const asset = {
              ...item,
              url:
                item.kind === 'video'
                  ? item.videoCover?.[0].url ?? videoPlaceholder.src
                  : item.thumbnailUrl ?? item.url,
              blur:
                'blur' in item
                  ? item.blur
                  : item.kind === 'video'
                    ? item.videoCover?.[0].blur ?? videoPlaceholder.blurDataURL
                    : '',

              width: 'width' in item ? item.width : item.kind === 'video' ? item.videoCover?.[0].width ?? 2 : 2,
              height: 'height' in item ? item.height : item.kind === 'video' ? item.videoCover?.[0].height ?? 1 : 1,
            }

            const remoteImage =
              unoptimizedRemoteImages && Number(item.url.match(/(rexsoftware)|(cloudfront)/)?.length) > 0

            return (
              <div
                key={item.id}
                className={twMerge(
                  'relative aspect-[--aspectRatio] shrink-0 grow-0 origin-bottom cursor-pointer duration-700',
                  show ? 'lg:opacity-100' : 'lg:opacity-0',
                  i === index
                    ? 'z-10 drop-shadow-[0_12px_8px_rgba(0,0,0,0.75)] -translate-y-1 scale-110'
                    : 'z-0 cursor-pointer drop-shadow-[0_3px_4px_rgba(0,0,0,0.5)] translate-y-0 scale-100 hover:duration-300 hover:-translate-y-1',
                )}
                style={{ '--aspectRatio': `${asset.width} / ${asset.height}` } as CSSProperties}
              >
                <Image
                  fill
                  src={asset.url}
                  alt={item.alt ?? ''}
                  title={item.caption ?? ''}
                  sizes="(max-width: 640px) 100px, 200px"
                  className="bg-black/50 object-cover object-[center_90%]"
                  unoptimized={remoteImage}
                  blurDataURL={asset.blur}
                  placeholder={asset.blur ? 'blur' : 'empty'}
                  onClick={() => sendToModalGallery({ type: 'Go to item', index: i })}
                />
              </div>
            )
          })}
        </div>
      </div>
      <div
        className={twMerge(
          show
            ? 'pointer-events-auto opacity-100 delay-1000 duration-500'
            : 'pointer-events-none opacity-0 delay-0 duration-300',
        )}
      >
        <CloseButton className="xlg:translate-x-full fixed -right-1/12 top-10 z-10 h-auto w-auto p-4 -translate-x-full sm:right-0" />
        <NavButton
          className="fixed left-0 top-1/2 translate-x-full lg:left-1/12 lg:-translate-x-full"
          direction="prev"
          disabled={prevDisabled}
          onClick={() => sendToModalGallery({ type: 'Go to previous item' })}
        />
        <NavButton
          className="fixed right-0 top-1/2 -translate-x-full lg:right-1/12 lg:translate-x-full"
          direction="next"
          disabled={nextDisabled}
          onClick={() => sendToModalGallery({ type: 'Go to next item' })}
        />
      </div>
      {active && <PreventScroll />}
    </div>
  )
}
